<template>
  <div class="course-materials-panel">
    <div class="panel-header">
      <h3>
        <i class="fas fa-folder-open"></i>
        Course Materials
      </h3>
      <p class="panel-subtitle">All downloadable</p>
    </div>

    <!-- Material Type Filter -->
    <div v-if="hasMultipleTypes" class="material-filters">
      <button
        v-for="type in materialTypes"
        :key="type"
        class="filter-btn"
        :class="{ active: selectedType === type }"
        @click="selectedType = type"
      >
        <i :class="getTypeIcon(type)"></i>
        {{ type }}
      </button>
    </div>

    <!-- Search Materials -->
    <div v-if="materials.length > 5" class="material-search">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search materials..."
        class="search-input"
      />
      <i class="fas fa-search search-icon"></i>
    </div>

    <!-- Materials List -->
    <div v-if="filteredMaterials.length > 0" class="materials-list">
      <!-- All Materials - List View (matching Topics UX) -->
      <div class="materials-list-view">
        <div
          v-for="material in filteredMaterials"
          :key="material.id || material.url"
          class="material-item"
        >
          <div class="material-icon">
            <i :class="getFileIcon(material.type)"></i>
          </div>
          <div class="material-content">
            <h4>{{ material.title || material.filename || 'Untitled Material' }}</h4>
            <p v-if="material.description" class="material-description">{{ material.description }}</p>
            <div class="material-meta">
              <span class="file-type">{{ material.type.toUpperCase() }}</span>
              <span v-if="material.file_size" class="file-size">{{ formatFileSize(material.file_size) }}</span>
            </div>
          </div>
          <button @click.stop="downloadMaterial(material)" class="download-btn-icon" title="Download">
            <i class="fas fa-download"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <i class="fas fa-folder-open"></i>
      <p>No course materials available</p>
    </div>

    <!-- Bulk Download -->
    <div v-if="materials.length > 1" class="bulk-actions">
      <button @click="downloadAll" class="btn btn-primary btn-block">
        <i class="fas fa-download"></i>
        Download All ({{ materials.length }})
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'CourseMaterialsPanel',
  props: {
    materials: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const selectedType = ref('all')
    const searchQuery = ref('')
    const currentPage = ref(1)
    const itemsPerPage = 12

    const materialTypes = computed(() => {
      const types = new Set(['all'])
      props.materials.forEach(m => types.add(m.type))
      return Array.from(types)
    })

    const hasMultipleTypes = computed(() => materialTypes.value.length > 2)

    const filteredMaterials = computed(() => {
      let filtered = props.materials

      // Filter by type
      if (selectedType.value !== 'all') {
        filtered = filtered.filter(m => m.type === selectedType.value)
      }

      // Filter by search
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(m => 
          m.title.toLowerCase().includes(query) ||
          (m.description && m.description.toLowerCase().includes(query))
        )
      }

      return filtered
    })

    const filteredSlides = computed(() => {
      return filteredMaterials.value.filter(m => 
        m.type === 'slide' || m.type === 'image'
      )
    })

    const filteredDocuments = computed(() => {
      return filteredMaterials.value.filter(m => 
        m.type === 'pdf' || m.type === 'document'
      )
    })

    const totalPages = computed(() => {
      return Math.ceil(filteredSlides.value.length / itemsPerPage)
    })

    const paginatedSlides = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage
      const end = start + itemsPerPage
      return filteredSlides.value.slice(start, end)
    })

    const getTypeIcon = (type) => {
      const icons = {
        all: 'fas fa-th',
        slide: 'fas fa-file-powerpoint',
        pdf: 'fas fa-file-pdf',
        image: 'fas fa-file-image',
        document: 'fas fa-file-alt'
      }
      return icons[type] || 'fas fa-file'
    }

    const getFileIcon = (type) => {
      const icons = {
        pdf: 'fas fa-file-pdf',
        document: 'fas fa-file-word',
        slide: 'fas fa-file-powerpoint',
        image: 'fas fa-file-image'
      }
      return icons[type] || 'fas fa-file'
    }

    const viewMaterial = (material) => {
      if (material.url) {
        window.open(material.url, '_blank')
      }
    }

    const downloadMaterial = (material) => {
      if (material.url) {
        const link = document.createElement('a')
        link.href = material.url
        link.download = material.title
        link.click()
      }
    }

    const downloadAll = () => {
      props.materials.forEach(material => {
        setTimeout(() => downloadMaterial(material), 100)
      })
    }

    const formatFileSize = (bytes) => {
      if (!bytes) return ''
      if (typeof bytes === 'string') return bytes // Already formatted
      
      const kb = bytes / 1024
      const mb = kb / 1024
      
      if (mb > 1) {
        return `${mb.toFixed(2)} MB`
      } else if (kb > 1) {
        return `${kb.toFixed(2)} KB`
      }
      return `${bytes} B`
    }

    return {
      selectedType,
      searchQuery,
      currentPage,
      materialTypes,
      hasMultipleTypes,
      filteredMaterials,
      paginatedSlides,
      filteredDocuments,
      totalPages,
      getTypeIcon,
      getFileIcon,
      viewMaterial,
      downloadMaterial,
      downloadAll,
      formatFileSize
    }
  }
}
</script>

<style scoped>
.course-materials-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--color-surface, #fff);
  border-radius: 8px;
  padding: 20px;
  overflow-y: auto;
}

.panel-header {
  margin-bottom: 16px;
}

.panel-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text, #111);
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-subtitle {
  font-size: 13px;
  color: var(--color-text-light, #666);
  margin: 0;
}

.material-filters {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 6px 12px;
  border: 1px solid var(--color-border, #e0e0e0);
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.filter-btn:hover {
  border-color: var(--color-primary, #00a79d);
  color: var(--color-primary, #00a79d);
}

.filter-btn.active {
  background: var(--color-primary, #00a79d);
  color: #fff;
  border-color: var(--color-primary, #00a79d);
}

.material-search {
  position: relative;
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid var(--color-border, #e0e0e0);
  border-radius: 6px;
  font-size: 14px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-light, #666);
}

.materials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.material-card {
  background: #fff;
  border: 1px solid var(--color-border, #e0e0e0);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.material-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.material-thumbnail {
  position: relative;
  width: 100%;
  padding-top: 75%;
  background: #f5f5f5;
  overflow: hidden;
}

.material-thumbnail img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #ccc;
}

.material-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  color: #fff;
  font-size: 24px;
}

.material-card:hover .material-overlay {
  opacity: 1;
}

.material-info {
  padding: 8px;
}

.material-info h4 {
  font-size: 13px;
  margin: 0 0 4px 0;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.material-description {
  font-size: 11px;
  color: var(--color-text-light, #666);
  margin: 0 0 6px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.material-details {
  display: flex;
  gap: 8px;
  font-size: 11px;
}

.page-number,
.file-size {
  color: var(--color-text-light, #666);
}

.download-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--color-primary, #00a79d);
  color: #fff;
  border: none;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;
}

.material-card:hover .download-btn {
  opacity: 1;
}

.materials-list-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.material-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border: 2px solid var(--color-border, #e0e0e0);
  border-left: 3px solid transparent;
  border-radius: 6px;
  transition: all 0.2s;
  position: relative;
}

.material-item:hover {
  background: rgba(0, 167, 157, 0.05);
  border-left-color: var(--color-primary, #00a79d);
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 167, 157, 0.1);
}

.material-icon {
  font-size: 28px;
  color: var(--color-primary, #00a79d);
  width: 40px;
  text-align: center;
  flex-shrink: 0;
  margin-top: 4px;
}

.material-content {
  flex: 1;
  min-width: 0;
}

.material-content h4 {
  font-size: 0.95rem;
  margin: 0 0 6px 0;
  font-weight: 600;
  color: var(--color-text, #111);
  line-height: 1.3;
}

.material-description {
  font-size: 0.85rem;
  color: var(--color-text-light, #666);
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.material-meta {
  display: flex;
  gap: 10px;
  font-size: 0.75rem;
  color: var(--color-text-light, #666);
  flex-wrap: wrap;
  align-items: center;
}

.file-type {
  background: var(--color-primary, #00a79d);
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
}

.file-size {
  color: var(--color-text-light, #999);
  font-size: 0.75rem;
}

.download-btn-icon {
  background: var(--color-primary, #00a79d);
  color: #fff;
  border: none;
  border-radius: 6px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.download-btn-icon:hover {
  background: var(--color-secondary, #5ac8c0);
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(0, 167, 157, 0.3);
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid var(--color-border, #e0e0e0);
  border-bottom: 1px solid var(--color-border, #e0e0e0);
  margin-bottom: 16px;
}

.page-btn {
  background: #fff;
  border: 1px solid var(--color-border, #e0e0e0);
  border-radius: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: var(--color-primary, #00a79d);
  color: #fff;
  border-color: var(--color-primary, #00a79d);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: var(--color-text, #111);
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-light, #666);
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.bulk-actions {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid var(--color-border, #e0e0e0);
}

.btn-block {
  width: 100%;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
</style>

